<template>
  <div>
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>

    <!--  Feature not enabled-->
    <div v-if="feature_not_enabled" class="mt-3" >
      <div class="font-icon-detail">
            <span style="font-size: 3em">
              <i class="fas fa-exclamation-triangle"></i>
            </span>
        <h4 class="mt-3">This feature is unavailable.</h4>
        <base-button type="primary" v-if="hasPermissions('billing_view')"
                     v-on:click="goToBilling()" class="mt-3">Go to billing</base-button>
        <p style="font-size: medium" v-else>Contact a site administrator to update billing.</p>
      </div>
    </div>

    <div v-if="!feature_not_enabled">
      <!--    Fonts-->
      <div v-for="(f, i) in fonts" :key="'F' + i">
        <link rel="stylesheet" :href="'https://fonts.googleapis.com/css?family=' + f.family">
      </div>

      <!-- Breadcrumb navigation -->
      <breadcrumb style="margin-left: -15px">
        <breadcrumb-item>
          <a href="/bookings/settings/list">Booking site templates</a>
        </breadcrumb-item>
        <breadcrumb-item>
          <span>{{ selectedBss.name || 'New template' }}</span>
        </breadcrumb-item>
      </breadcrumb>

      <div class="row">
        <div :class="mainColClass">
          <card card-body-classes="table-full-width">
            <!--  Tab switcher -->
            <div class="row mb-3">
              <div class="col">
                <tab-rail
                  :tabs="categories"
                  :value="activeTabIndex"
                  tight
                  @input="switchTab"
                >
                  <template #actions>
                    <badge class="mb-0" v-if="!selectedBss.is_active" type="danger">inactive</badge>
                    <badge class="mb-0" v-if="selectedBss.is_active" type="success">active</badge>
                    <el-tooltip content="More actions"
                                effect="light"
                                :open-delay="300"
                                placement="top">
                      <el-dropdown>
                        <base-button
                          link
                          type="primary"
                          size="sm"
                        > <i style="font-size: large" class="tim-icons el-icon-more"></i>
                        </base-button>
                        <template #dropdown>
                          <el-dropdown-menu>
                            <a class="dropdown-item mt-1 mb-2" target="_blank" :href="bookingSiteUrl">
                              <i class="tim-icons icon-world text-default mr-2"></i> Go to booking site
                            </a>
                            <a class="dropdown-item mt-1 mb-2" v-if="!selectedBss.is_default" href="#" @click.prevent="handleSetDefault(true)">
                              <i class="tim-icons icon-check-2 text-default mr-2"></i> Set as main
                            </a>
                            <a class="dropdown-item mt-1 mb-2" v-if="selectedBss.is_active" href="#" @click.prevent="activate(0)">
                              <span class="inactive-dot mr-2"></span> Deactivate
                            </a>
                            <a class="dropdown-item mt-1 mb-2" v-if="!selectedBss.is_active" href="#" @click.prevent="activate(1)">
                              <span class="active-dot mr-2"></span> Activate
                            </a>
                            <hr>
                            <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="archiveTemplatePrompt">
                              <i class="tim-icons icon-trash-simple text-default mr-2"></i> Archive
                            </a>
                          </el-dropdown-menu>
                        </template>
                      </el-dropdown>
                    </el-tooltip>
                  </template>
                </tab-rail>
              </div>
            </div>

            <!-- Publish tab -->
            <publish-tab
              v-if="activeTabIndex === 0"
              :selected-bss="selectedBss"
              :group="group"
              :errs="errs"
              :is-create-mode="isCreateMode"
              :booking-base-url="bookingBaseUrl"
              :embedded-text="embedded_text"
              @validate-slug="validateSlug"
              @handle-set-default="handleSetDefault"
              @copy="copy"
              @save="updateBss"
            ></publish-tab>

            <!-- Content tab -->
            <content-tab
              v-if="activeTabIndex === 1"
              :selected-bss="selectedBss"
              :games="games"
              :promos="promos"
              :all-group-games="all_group_games"
              :payment-settings="payment_settings"
              :group="group"
              :custom-fields="custom_fields"
              :is-create-mode="isCreateMode"
              :default-date-options="default_date_options"
              :timeout-periods="timeout_periods"
              :date-format-cal="date_format_cal"
              :errs="errs"
              :fonts="fonts"
              :ordered-fonts="orderedFonts"
              :fit-options="fitOptions"
              @section-changed="onContentSectionChanged"
              @update-site-content="updateSiteContent"
              @copy="copy"
              @activate="activate"
              @edit-custom-field="editCustomField"
              @edit-custom-field-behavior="editCustomFieldBehavior"
              @update-custom-field-order="updateCustomFieldOrder"
              @handle-preview="handlePreview"
              @save="updateBss"
            ></content-tab>


          </card>
        </div>

        <!-- Schematic preview (Content tab only) -->
        <div class="col-lg-4 col-12" v-if="activeTabIndex === 1">
          <schematic-preview
            :template="selectedBss"
            :events="games"
            :all-games="all_group_games"
            :custom-fields="custom_fields"
            :active-step="contentActiveSection"
            @step-selected="onContentSectionChanged"
            @open-live-preview="openLivePreview"
          ></schematic-preview>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {API_LOCATION, BOOKING_URL} from "@/api-config";
import axios from "axios";
import swal from "sweetalert2";
import {Auth} from "aws-amplify";
import {Switch, DatePicker, Select, Option, Tooltip, Dropdown, DropdownItem, DropdownMenu} from 'element-ui';
import {Badge} from 'src/components';
import _ from "lodash";
import currencies from "@/assets/currencies.json";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";

import TabRail from '@/components/TabRail.vue';
import ContentTab from './components/tabs/ContentTab.vue';
import PublishTab from './components/tabs/PublishTab.vue';
import SchematicPreview from './components/SchematicPreview.vue';

export default {
  name: "BookingSiteTemplateEditor",
  components: {
    Breadcrumb,
    BreadcrumbItem,
    ContentTab,
    PublishTab,
    SchematicPreview,
    TabRail,
    Badge,
    [Switch.name]: Switch,
    [DatePicker.name]: DatePicker,
    [Select.name]: Select,
    [Option.name]: Option,
    [Tooltip.name]: Tooltip,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [DropdownMenu.name]: DropdownMenu,
  },
  data() {
    return {
      group: JSON.parse(localStorage.getItem('group')),
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      date_format_cal: JSON.parse(localStorage.getItem('group')).date_format.replaceAll('Y', 'y').replaceAll('D', 'd'),
      t: 0,
      j: 0,
      timeout_periods: [{id: 0, label: 'minute(s)'}, {id: 1, label: 'hour(s)'}, {id: 2, label: 'day(s)'}],
      default_date_options: [
        {id: 0, label: 'Current date'},
        {id: 1, label: 'Days ahead'},
        {id: 2, label: 'Specific date'},
        {id: 3, label: 'Earliest available date'}
      ],
      feature_not_enabled: false,
      custom_field_types: [
        {id: 1, type: 'Checkbox'},
        {id: 2, type: 'Input field'},
        {id: 3, type: 'Dropdown'}
      ],
      custom_fields: [],
      loading: false,
      payment_settings: [],
      embedded_text: 'iframe',
      currencies: currencies.currencies,
      number_format: JSON.parse(localStorage.getItem('group')).number_format,
      currency_symbol: '$',
      fitOptions: ['cover', 'fill', 'contain', 'scale-down', 'none'],
      selectedBss: '',
      file: '',
      games: [],
      promos: [],
      all_group_games: [],
      fonts: '',
      bookingBaseUrl: 'https://offthecouch.io/book/',
      errs: {
        name: '',
        slug: '',
        redirect_url: '',
        redirect_button_text: '',
        default_specific_date: '',
        text_color: '',
        background_color: '',
        text_color_card: '',
        link_color: '',
        button_color: '',
        card_color: '',
        input_border_color: '',
        available_slot_background_color: '',
        available_slot_text_color: '',
        ctb_slot_background_color: '',
        ctb_slot_text_color: '',
        sold_out_slot_background_color: '',
        sold_out_slot_text_color: ''
      },
      contentActiveSection: 0,
      activeTabIndex: 0,
      isCreateMode: false,
    }
  },
  computed: {
    orderedFonts: function () {
      let fontsCopy = [...(this.fonts || [])]
      _.remove(fontsCopy, { family: 'Libre Baskerville' });
      _.remove(fontsCopy, { family: 'Material Icons' });
      _.remove(fontsCopy, { family: 'Material Icons Outlined' });
      _.remove(fontsCopy, { family: 'Material Icons Sharp' });
      _.remove(fontsCopy, { family: 'Material Icons Round' });
      _.remove(fontsCopy, { family: 'M PLUS Rounded 1c' });
      return _.orderBy(fontsCopy, 'family')
    },
    categories() {
      return [
        { name: 'Publish', icon: 'tim-icons icon-cloud-upload-94', active: this.activeTabIndex === 0 },
        { name: 'Content', icon: 'tim-icons icon-bullet-list-67', active: this.activeTabIndex === 1 },
      ];
    },
    mainColClass() {
      if (this.activeTabIndex === 1) {
        return 'col-lg-8 col-12'
      }
      return 'col-lg-12'
    },
    bookingSiteUrl() {
      let url = BOOKING_URL + this.group.code
      if (!this.selectedBss.is_default && this.selectedBss.slug) {
        url += '/' + this.selectedBss.slug
      }
      return url
    }
  },
  methods: {
    goToBilling(){
      this.$router.push('/settings/billing')
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    initializeNewTemplate() {
      this.isCreateMode = true
      this.selectedBss = {
        name: '',
        description: '',
        slug: '',
        layout_type_id: 1,
        list_image_size: 2,
        list_image_position: 1,
        is_default: false,
        is_active: 0,
        background_color: '#1e1e2f',
        text_color: '#ffffff',
        button_color: '#e14eca',
        card_color: '#27293d',
        text_color_card: '#ffffff',
        link_color: '#1d8cf8',
        input_border_color: '#7d7d7d99',
        font_family: 'Roboto',
        font_family_card: 'Roboto',
        show_logo: false,
        logo: '',
        logo_height: 60,
        logo_width: 0,
        logo_fit: 'contain',
        display_progress_bar: true,
        display_section_titles: true,
        show_language_switcher: false,
        allow_autoscroll: false,
        show_unavailable_slots: false,
        hide_custom_message_when_sold_out: false,
        show_promo_code_field: true,
        show_total_pricing_on_events: false,
        show_available_ticket_count: false,
        show_game_title: true,
        show_game_difficulty: true,
        show_game_duration: true,
        show_number_of_players: true,
        show_game_details_over_image: false,
        show_game_description: false,
        game_info_trigger_type: 'icon',
        game_info_trigger_icon: 'info-circle',
        game_info_trigger_text: 'Learn more',
        game_info_trigger_position: 'bottom-right',
        auto_redirect: false,
        redirect_url: 'https://offthecouch.io',
        redirect_button_text: 'Return to website',
        redirect_delay: 15,
        available_slot_background_color: '#e14eca',
        available_slot_text_color: '#ffffff',
        ctb_slot_background_color: '#1d8cf8',
        ctb_slot_text_color: '#ffffff',
        sold_out_slot_background_color: '#525f7f',
        sold_out_slot_text_color: '#ffffff',
        allow_request_to_book: false,
        request_to_book_text: 'Request to book',
        rtb_slot_background_color: '#FFD600',
        rtb_slot_text_color: '#1E1E2F',
        clear_stale_carts: true,
        cart_timeout_value: 15,
        cart_timeout_period: {id: 0, label: 'minute(s)'},
        show_booking_notice_countdown: false,
        booking_notice_countdown_minutes: 15,
        timeout_period: {id: 0, label: 'minute(s)'},
        default_days_ahead: 0,
        default_date_type: {id: 0, label: 'Current date'},
        title: '',
        subtext: '',
        addons_title: '',
        addons_subtext: '',
        show_upsell_section: false,
        upsell_title: '',
        upsell_subtext: '',
        upsell_promo_code_id: null,
        upsell_badge_text: '',
        upsell_position: 'above_form',
        upsell_promo_code_name: null,
        confirmation_message: '',
        insufficient_resource_message: '',
        unblock_incomplete_bookings: false,
        timeout_value: 15,
        show_custom_promo_code_error_message: false,
        promo_code_placeholder_text: '',
        promo_code_field_position: 'all_steps',
        promo_code_error_message: '',
        display_waiver_link: false,
        hide_date_selection: false,
        show_calendar_link: false,
        gift_cards_enabled: false,
        gift_card_unique_link_enabled: false,
        booked_text: 'Sold out',
        blocked_text: 'Sold out',
        call_to_book_text: 'Call To Book',
        phone_field_mode: 'required',
        zip_field_mode: 'hidden',
        address_field_mode: 'hidden',
        deposit_layout_type: 'checkbox',
        enable_deposit_text: false,
        deposit_text: '',
      }
      this.games = []
      this.custom_fields = []
      this.getIframe()
      // Load library fields so user can attach them locally; create lambda will persist on save
      this.loadGroupCustomFieldsForCreate()
    },
    // Live promo codes of the group (plus cross group codes) for the upsell section picker
    async loadPromos() {
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.group.id + '/promos?minimal=1&archived=0'
      try {
        const res = await axios.get(url, config)
        this.promos = res.data.promos || []
      } catch (err) {
        console.error('Failed to load promo codes', err)
        this.$notify({ message: 'Could not load promo codes', timeout: 4000, icon: '', horizontalAlign: 'right', verticalAlign: 'top', type: 'danger' })
      }
    },
    async loadGroupCustomFieldsForCreate() {
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.group.id + '/custom-fields?count=1000&offset=0&archived=0'
      try {
        const res = await axios.get(url, config)
        const groupFields = res.data.custom_fields || res.data || []
        this.custom_fields = groupFields.map(cf => ({
          type: _.find(this.custom_field_types, { id: cf.type }),
          label: cf.label,
          id: cf.id,
          active: 0,
          position: 999,
          required: cf.required,
          add_alert: cf.add_alert,
          interaction_required: cf.interaction_required,
          dropdown_options: cf.dropdown_options || [],
          external_links: cf.external_links || [],
          policies: cf.policies || [],
        }))
      } catch (err) {
        console.error('Failed to load library custom fields for new template', err)
      }
    },
    validateSlug() {
      if (!this.selectedBss.slug || this.selectedBss.is_default) {
        this.errs.slug = ''
        return true
      }
      const slug = this.selectedBss.slug.toLowerCase().trim()
      const slugRegex = /^[a-z][a-z0-9-]{1,99}$/
      if (!slugRegex.test(slug)) {
        this.errs.slug = 'Use lowercase letters, numbers, and hyphens only. Must start with a letter.'
        return false
      }
      this.errs.slug = ''
      this.selectedBss.slug = slug
      return true
    },
    async handleSetDefault(newValue) {
      if (!newValue) {
        if (!this.isCreateMode) {
          this.selectedBss.is_default = true
        }
        return
      }
      if (this.isCreateMode) {
        this.selectedBss.slug = ''
        return
      }
      try {
        const result = await swal({
          title: 'Set as main template?',
          text: 'This will make this template the default booking page. The current default will no longer be the default.',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, set as default'
        })
        if (result.value === true) {
          await this.setAsDefault()
        } else {
          this.selectedBss.is_default = false
        }
      } catch (e) {
        this.selectedBss.is_default = false
      }
    },
    async setAsDefault() {
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token }}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/booking-site-settings/' + this.selectedBss.id + '/set-default'

      axios.put(url, {}, config)
        .then((res) => {
          this.loading = false
          swal('Success', 'This template is now the default booking page', 'success')
          this.getBookingSiteSettings(true)
        })
        .catch((err) => {
          this.loading = false
          this.selectedBss.is_default = false
          console.error(err)
          swal('Error!', 'Something went wrong', 'error')
        })
    },
    async createTemplate() {
      let errors = 0

      if (!this.selectedBss.name || this.selectedBss.name.trim() === '') {
        this.errs.name = 'Template name is required'
        errors++
      } else {
        this.errs.name = ''
      }

      if (!this.selectedBss.is_default) {
        if (!this.selectedBss.slug || !this.selectedBss.slug.trim()) {
          this.errs.slug = 'Slug is required for non-default templates'
          errors++
        } else if (!this.validateSlug()) {
          errors++
        }
      } else {
        this.errs.slug = ''
      }

      if (errors > 0) {
        this.activeTabIndex = 0
        return
      }

      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/booking-site-settings'

      let payload = {
        bss: {
          ...this.selectedBss,
          default_date_type: this.selectedBss.default_date_type ? this.selectedBss.default_date_type.id : 0,
          timeout_period: this.selectedBss.timeout_period ? this.selectedBss.timeout_period.id : 0,
          cart_timeout_period: this.selectedBss.cart_timeout_period ? this.selectedBss.cart_timeout_period.id : 0,
          is_default: this.selectedBss.is_default ? 1 : 0
        },
        games: this.games.filter(g => g.is_active == 1).map((g, i) => ({
          game_id: g.game_id,
          position: i + 1
        })),
        custom_fields: this.custom_fields
          .filter(cf => cf.active === 1)
          .sort((a, b) => (a.position || 0) - (b.position || 0))
          .map((cf, i) => ({
            id: cf.id,
            position: i,
            required: cf.required ? 1 : 0,
            interaction_required: cf.interaction_required ? 1 : 0,
            add_alert: cf.add_alert ? 1 : 0
          }))
      }
      // Display only (picker label for an archived linked code)
      delete payload.bss.upsell_promo_code_name

      axios.post(url, payload, config)
        .then((res) => {
          this.loading = false
          this.isCreateMode = false

          this.selectedBss.id = res.data.id

          this.$router.replace({ query: { id: res.data.id }})

          swal('Success', 'Booking site template created. You can now configure the settings.', 'success')
        })
        .catch((err) => {
          this.loading = false
          console.error(err)
          if(err.response && err.response.status == 401){
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          }
          else if(err.response && err.response.status == 409){
            swal({
              title: 'Duplicate name',
              html: 'A template with a similar name already exists. Please choose a different name.',
              type: 'warning',
            })
          }
          else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    updateSiteContentLocal(action, item) {
      if (action === 'add_to_site') {
        const alreadyExists = this.games.find(g => g.game_id === (item.game_id || item.id))
        if (alreadyExists) {
          alreadyExists.position = this.games.filter(g => g.is_active == 1).length + 1
          alreadyExists.is_active = 1
        } else {
          this.games.push({
            game_id: item.game_id || item.id,
            name: item.name,
            is_active: 1,
            has_unique_link: 0,
            position: this.games.filter(g => g.is_active == 1).length + 1
          })
        }
      } else if (action === 'remove_from_site') {
        const game = this.games.find(g => g.game_id === item.game_id)
        if (game) {
          game.is_active = 0
        }
      } else if (action === 'update_order') {
        const activeGames = this.games.filter(g => g.is_active == 1)
        activeGames.sort((a, b) => a.position - b.position)
        activeGames.forEach((game, i) => {
          game.position = i + 1
        })
      }
    },
    updateSiteContent(action, item){
      this.updateSiteContentLocal(action, item)
    },
    async editCustomFieldBehavior(field){
      // In create mode, behavior lives in local state until the template is saved
      if (this.isCreateMode) return
      this.$set(field, '_saving', true)
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.group.id + '/booking-site-settings/' + this.selectedBss.id + '/custom-fields?id=' + field.id

      let data = {
        action: 'set_behavior',
        required: field.required,
        interaction_required: field.interaction_required,
        add_alert: field.add_alert
      }

      try {
        await axios.put(url, data, config)
      } catch (err) {
        console.error(err)
        this.$notify({ type: 'danger', message: 'Failed to update field behavior' })
        this.getBookingSiteSettings(true)
      } finally {
        this.$set(field, '_saving', false)
      }
    },
    async editCustomField(action, item){
      // Optimistic local update — row appears/disappears immediately
      if (action === 'activate') item.active = 1
      else if (action === 'deactivate') item.active = 0

      // In create mode, the activate/deactivate state lives locally until the create lambda persists it
      if (this.isCreateMode) return

      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.group.id + '/booking-site-settings/' + this.selectedBss.id + '/custom-fields?id='+ item.id

      let data = {
        action: action
      }
      try {
        await axios.put(url, data, config)
        const successMsg = action === 'activate' ? 'Custom field added'
          : action === 'deactivate' ? 'Custom field removed'
          : 'Custom field updated'
        this.$notify({ type: 'success', message: successMsg })
        this.getBookingSiteSettings(true)
      } catch (err) {
        console.error(err)
        if(err.response && err.response.status == 401){
          this.$notify({ type: 'danger', message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', timeout: 8000 })
        }
        else if(err.response && err.response.status == 500){
          this.$notify({ type: 'danger', message: 'This feature is unavailable. Update billing to continue.', timeout: 8000 })
        }
        else {
          this.$notify({ type: 'danger', message: 'Something went wrong' })
        }
        // Roll back optimistic update by refetching
        this.getBookingSiteSettings(true)
      }
    },
    async updateCustomFieldOrder(customFields){
      // In create mode, order is held in local state until the template is saved
      if (this.isCreateMode) return
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.group.id + '/booking-site-settings/' + this.selectedBss.id + '/custom-fields'

      let data = {
        custom_fields: customFields || this.custom_fields,
        action: 'reorder'
      }

      axios.put(url, data, config)
        .then((res) => {
        })
        .catch((err) => {
          console.error(err)
          this.$notify({ type: 'danger', message: 'Failed to reorder custom fields' })
          this.getBookingSiteSettings(true)
        })
    },
    onContentSectionChanged(sectionIndex) {
      this.contentActiveSection = sectionIndex
    },
    openLivePreview() {
      window.open(this.bookingSiteUrl, '_blank')
    },
    switchTab(index){
      this.activeTabIndex = index
      this.j++
    },
    copy(item) {
      navigator.clipboard.writeText(item);
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    handlePreview(event){
      let file = event.target.files[0];
      this.selectedBss.logo = URL.createObjectURL(file);
      this.file = file
    },
    async saveFile() {
      this.uploadUrl = await this.getSignedURL(this.file)
      let uploaded = await this.uploadFile(this.file)
    },
    async getSignedURL (file) {
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'upload'

      let payload = {
        filePath: file.name,
        contentType: file.type
      }

      return new Promise ( function(resolve) {
        axios.post(url, payload, config)
          .then((res) => {
            resolve(res.data)
          })
          .catch((err) => {
            swal({
              title: 'Error!',
              text: `You must upload a player photo before saving!`,
              type: 'error',
              confirmButtonColor: '#fd5d93',
              confirmButtonText: 'Try Again'
            })
            console.error(err)
          })
      })
    },
    uploadFile (file) {
      let url = this.uploadUrl
      let config = { headers: {
          'Content-Type': file.type
        }}

      let payload = file
      return new Promise ( function(resolve) {
        axios.put(url, payload, config)
          .then((res) => {
            resolve(res)
          })
          .catch((err) => {
            console.error(err)
          })
      })
    },
    isValidUrl(url){
      try {
        let parsedURL = new URL(url);
        return parsedURL.protocol === "http:" || parsedURL.protocol === "https:";
      } catch (error) {
        return false;
      }
    },
    async updateBss(){
      if (this.isCreateMode) {
        return this.createTemplate()
      }

      let publishErrors = 0
      let contentErrors = 0

      if(!this.selectedBss.name || this.selectedBss.name.trim() === ''){
        this.errs.name = 'Template name is required'
        publishErrors++
      } else {
        this.errs.name = ''
      }

      if (!this.selectedBss.is_default) {
        if (!this.selectedBss.slug || !this.selectedBss.slug.trim()) {
          this.errs.slug = 'Slug is required for non-default templates'
          publishErrors++
        } else if (!this.validateSlug()) {
          publishErrors++
        }
      } else {
        this.errs.slug = ''
      }

      if(!this.isValidUrl(this.selectedBss.redirect_url)){
        this.errs.redirect_url = 'Please enter a valid URL'
        contentErrors++
      } else {
        this.errs.redirect_url = ''
      }

      if(this.selectedBss.auto_redirect == 0 && this.selectedBss.redirect_button_text == ''){
        this.errs.redirect_button_text = 'Button text cannot be empty'
        contentErrors++
      } else {
        this.errs.redirect_button_text = ''
      }

      if(this.selectedBss.default_date_type && this.selectedBss.default_date_type.id === 2 && !this.selectedBss.default_specific_date){
        this.errs.default_specific_date = 'Please select a date'
        contentErrors++
      } else {
        this.errs.default_specific_date = ''
      }

      const colorFields = [
        { field: 'text_color', label: 'Text color' },
        { field: 'background_color', label: 'Background color' },
        { field: 'text_color_card', label: 'Card text color' },
        { field: 'link_color', label: 'Link color' },
        { field: 'button_color', label: 'Button color' },
        { field: 'card_color', label: 'Card background color' },
        { field: 'available_slot_background_color', label: 'Available slot background color' },
        { field: 'available_slot_text_color', label: 'Available slot text color' },
        { field: 'ctb_slot_background_color', label: 'Call to book slot background color' },
        { field: 'ctb_slot_text_color', label: 'Call to book slot text color' },
        { field: 'sold_out_slot_background_color', label: 'Sold out slot background color' },
        { field: 'sold_out_slot_text_color', label: 'Sold out slot text color' },
        { field: 'rtb_slot_background_color', label: 'Request to book slot background color' },
        { field: 'rtb_slot_text_color', label: 'Request to book slot text color' }
      ]

      colorFields.forEach(({ field, label }) => {
        if (this.selectedBss[field] === '' || this.selectedBss[field] === null || this.selectedBss[field] === undefined) {
          this.errs[field] = `${label} is required`
          contentErrors++
        } else {
          this.errs[field] = ''
        }
      })

      if(publishErrors > 0 || contentErrors > 0){
        if (publishErrors > 0) {
          this.activeTabIndex = 0
        } else {
          this.activeTabIndex = 1
        }
        return
      }

      if(this.file != ''){
        await this.saveFile()
        this.selectedBss.logo = this.uploadUrl.split("?")[0]
      }

      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let groupId = this.group.id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId + '/booking-site-settings/' + this.selectedBss.id

      let bssPayload = {
        ...this.selectedBss,
        default_date_type: this.selectedBss.default_date_type ? this.selectedBss.default_date_type.id : 0
      }
      // Display only (picker label for an archived linked code)
      delete bssPayload.upsell_promo_code_name

      let payload = {
        'bss': bssPayload,
        'games': this.games
      }
      this.loading = true
      axios.put(url, payload, config)
        .then((res) => {
          this.loading = false
          swal({
            title: 'Success!',
            text: `Booking site settings have been updated`,
            type: 'success'
          })

          this.getBookingSiteSettings()
        })
        .catch((err) => {
          this.loading = false
          console.error(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          }
        })
    },
    async activate(activate){
      if(activate){
        if(this.games.filter(g => g.is_active == 1).length <= 0 && !this.selectedBss.gift_cards_enabled){
          swal({
            title: 'Error!',
            text: `You need at least one event or gift cards enabled to activate your booking site.`,
            type: 'error',
            confirmButtonColor: '#fd5d93',
            confirmButtonText: 'Confirm'
          })
          return
        }
        if(!this.payment_settings || !this.payment_settings.length){
          swal({
            title: 'Error!',
            text: `You need to set up a payment gateway before activating your booking site.`,
            type: 'error',
            confirmButtonColor: '#fd5d93',
            confirmButtonText: 'Confirm'
          })
          return
        }
      }
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.group.id + '/booking-site-settings/' + this.selectedBss.id + '/activate'

      let payload = {
        'is_active': activate
      }

      this.selectedBss.is_active = activate

      axios.put(url, payload, config)
        .then((res) => {
          this.loading = false
          if(activate == 1){
            swal({
              title: 'Success',
              text: `Booking site has been activated`,
              type: 'success',
              confirmButtonClass: 'btn btn-success btn-fill',
              confirmButtonText: 'Ok',
              buttonsStyling: false
            })
          } else {
            swal({
              title: 'Success',
              text: `Booking site has been deactivated`,
              type: 'success',
              confirmButtonClass: 'btn btn-success btn-fill',
              confirmButtonText: 'Ok',
              buttonsStyling: false
            })
          }
        })
        .catch((err) => {
          this.loading = false
          console.error(err)
          if(err.response.status == 401){
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          }
          else if(err.response.status == 500){
            swal({
              title: 'Error',
              html: 'The feature has not been enabled as part of your subscription plan.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          }
          else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    archiveTemplatePrompt() {
      let message = `This will archive the "${this.selectedBss.name}" template. You can restore it later from the Archived tab.`
      if (this.selectedBss.is_default) {
        message += ' Note: This is currently your main booking page. After archiving, visitors to your base booking URL will see no active template.'
      }
      swal({
        title: 'Are you sure?',
        text: message,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, archive it'
      }).then((response) => {
        if (response.value === true) {
          this.archiveTemplate()
        }
      })
    },
    async archiveTemplate() {
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token }}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.group.id + '/booking-site-settings/' + this.selectedBss.id

      axios.delete(url, config)
        .then((res) => {
          this.loading = false
          swal('Archived!', 'Template has been archived', 'success')
          this.$router.push('/bookings/settings/list')
        })
        .catch((err) => {
          this.loading = false
          console.error(err)
          swal('Error!', 'Something went wrong', 'error')
        })
    },
    getIframe(){
      let bookingPath = this.group.code
      if (!this.selectedBss.is_default && this.selectedBss.slug) {
        bookingPath += '/' + this.selectedBss.slug
      }
      this.embedded_text = `<div id="otcContainer"><\/div><script>(function(){var uk=["utm_source","utm_medium","utm_campaign"];function gC(n){var m=document.cookie.match(new RegExp("(?:^|; )"+n+"=([^;]*)"));return m?decodeURIComponent(m[1]):null}function sC(n,v){var d=new Date;d.setTime(d.getTime()+30*24*60*60*1000);document.cookie=n+"="+encodeURIComponent(v)+";expires="+d.toUTCString()+";path=/;SameSite=Lax"}var p=new URLSearchParams(window.location.search);var hasNew=false;uk.forEach(function(k){var v=p.get(k);if(v){hasNew=true}});if(hasNew){uk.forEach(function(k){var v=p.get(k);if(v)sC("otc_"+k,v)})}var q=new URLSearchParams(window.location.search);uk.forEach(function(k){if(!q.get(k)){var c=gC("otc_"+k);if(c)q.set(k,c)}});var qs=q.toString();var f=document.createElement("iframe");f.src="https://offthecouch.io/book/` + bookingPath + `"+(qs?"?"+qs:"");var scrollTop=0;f.width="100%";f.allow="payment";f.scrolling="no";f.style.border="none";f.style.display="block";f.style.overflow="hidden";f.style.height="auto";window.addEventListener("message",function(e){if(e.origin==="https://offthecouch.io"&&e.data.action==="updateHeight")f.style.height=e.data.height+"px";if(e.origin==="https://offthecouch.io"&&e.data.action==="scrollToTop"){scrollTop=findPosY(document.getElementById("otcContainer"));window.scrollTo({top:scrollTop-30,behavior:"smooth"})}if(e.origin==="https://offthecouch.io")switch(e.data.eventType){case"page_view":insertGoogleAnalyticsScript(e.data.id);break;case"add_to_cart":window.gtag&&gtag("event","add_to_cart",{currency:e.data.currency,value:Number(e.data.eventValue)||0});break;case"begin_checkout":window.gtag&&gtag("event","begin_checkout",{currency:e.data.currency,value:Number(e.data.eventValue)||0});break;case"purchase":window.gtag&&gtag("event","purchase",{currency:e.data.currency,value:Number(e.data.eventValue)||0});break;case"success":window.gtag&&gtag("event","success",{currency:e.data.currency,value:Number(e.data.eventValue)||0});break;default:}});document.getElementById("otcContainer").appendChild(f);function findPosY(o){var t=0;if(o.offsetParent){while(o.offsetParent){t+=o.offsetTop;o=o.offsetParent}}else if(o.y){t+=o.y}return t}function insertGoogleAnalyticsScript(i){if(!i)return;window.dataLayer=window.dataLayer||[];window.gtag=window.gtag||function(){dataLayer.push(arguments)};if(!window.__otcGaConfigured){window.__otcGaConfigured=true;if(!document.querySelector('script[src^="https://www.googletagmanager.com/gtag/js"]')){var s=document.createElement("script");s.async=true;s.src="https://www.googletagmanager.com/gtag/js?id="+i;document.head.appendChild(s)}window.gtag("js",new Date);window.gtag("config",i)}window.gtag("event","page_view")}})()<\/script>`
    },
    async getBookingSiteSettings(silent){
      let settingsId = this.$route.query.id

      if (!silent) this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + this.group.id + '/booking-site-settings/' + settingsId

      axios.get(url, config)
        .then((res) => {
          localStorage.removeItem('editing')
          // Spread BEFORE assignment so fields missing from the response (e.g. the
          // customer-info field modes) are reactive — post-assignment adds are not
          this.selectedBss = {
            ...res.data.booking_site_settings,
            input_border_color: res.data.booking_site_settings.input_border_color || '#7d7d7d99',
            phone_field_mode: res.data.booking_site_settings.phone_field_mode || 'required',
            zip_field_mode: res.data.booking_site_settings.zip_field_mode || 'hidden',
            address_field_mode: res.data.booking_site_settings.address_field_mode || 'hidden',
            deposit_layout_type: res.data.booking_site_settings.deposit_layout_type || 'checkbox',
            enable_deposit_text: Boolean(res.data.booking_site_settings.enable_deposit_text),
            deposit_text: res.data.booking_site_settings.deposit_text || '',
            addons_title: res.data.booking_site_settings.addons_title || '',
            addons_subtext: res.data.booking_site_settings.addons_subtext || '',
            show_upsell_section: Boolean(res.data.booking_site_settings.show_upsell_section),
            upsell_title: res.data.booking_site_settings.upsell_title || '',
            upsell_subtext: res.data.booking_site_settings.upsell_subtext || '',
            upsell_promo_code_id: res.data.booking_site_settings.upsell_promo_code_id || null,
            upsell_badge_text: res.data.booking_site_settings.upsell_badge_text || '',
            upsell_position: res.data.booking_site_settings.upsell_position || 'above_form',
            promo_code_field_position: res.data.booking_site_settings.promo_code_field_position || 'all_steps',
            upsell_promo_code_name: res.data.booking_site_settings.upsell_promo_code_name || null,
            show_game_description: Boolean(res.data.booking_site_settings.show_game_description),
            game_info_trigger_type: res.data.booking_site_settings.game_info_trigger_type || 'icon',
            game_info_trigger_icon: res.data.booking_site_settings.game_info_trigger_icon || 'info-circle',
            game_info_trigger_text: res.data.booking_site_settings.game_info_trigger_text || 'Learn more',
            game_info_trigger_position: res.data.booking_site_settings.game_info_trigger_position || 'bottom-right',
            show_booking_notice_countdown: Boolean(res.data.booking_site_settings.show_booking_notice_countdown),
            booking_notice_countdown_minutes: res.data.booking_site_settings.booking_notice_countdown_minutes != null
              ? res.data.booking_site_settings.booking_notice_countdown_minutes
              : 15,
            allow_request_to_book: Boolean(res.data.booking_site_settings.allow_request_to_book),
            request_to_book_text: res.data.booking_site_settings.request_to_book_text || 'Request to book',
            rtb_slot_background_color: res.data.booking_site_settings.rtb_slot_background_color || '#FFD600',
            rtb_slot_text_color: res.data.booking_site_settings.rtb_slot_text_color || '#1E1E2F'
          }
          this.selectedBss.is_default = Boolean(this.selectedBss.is_default)
          this.selectedBss.allow_autoscroll = Boolean(this.selectedBss.allow_autoscroll)
          this.selectedBss.display_progress_bar = Boolean(this.selectedBss.display_progress_bar)
          this.selectedBss.show_language_switcher = Boolean(this.selectedBss.show_language_switcher)
          this.selectedBss.display_waiver_link = Boolean(this.selectedBss.display_waiver_link)
          this.selectedBss.auto_redirect = Boolean(this.selectedBss.auto_redirect)
          this.selectedBss.show_unavailable_slots = Boolean(this.selectedBss.show_unavailable_slots)
          this.selectedBss.display_section_titles = Boolean(this.selectedBss.display_section_titles)
          this.selectedBss.show_promo_code_field = Boolean(this.selectedBss.show_promo_code_field)
          this.selectedBss.show_custom_promo_code_error_message = Boolean(this.selectedBss.show_custom_promo_code_error_message)
          this.selectedBss.show_total_pricing_on_events = Boolean(this.selectedBss.show_total_pricing_on_events)
          this.selectedBss.show_available_ticket_count = Boolean(this.selectedBss.show_available_ticket_count)
          this.selectedBss.unblock_incomplete_bookings = Boolean(this.selectedBss.unblock_incomplete_bookings)
          this.selectedBss.clear_stale_carts = Boolean(this.selectedBss.clear_stale_carts)
          this.selectedBss.gift_cards_enabled = Boolean(this.selectedBss.gift_cards_enabled)
          this.selectedBss.gift_card_unique_link_enabled = Boolean(this.selectedBss.gift_card_unique_link_enabled)
          this.selectedBss.show_logo = Boolean(this.selectedBss.show_logo)
          this.selectedBss.hide_date_selection = Boolean(this.selectedBss.hide_date_selection)
          this.selectedBss.show_calendar_link = Boolean(this.selectedBss.show_calendar_link)
          this.selectedBss.enable_real_time_updates = Boolean(this.selectedBss.enable_real_time_updates)
          this.selectedBss.show_game_details_over_image = Boolean(this.selectedBss.show_game_details_over_image)
          this.selectedBss.show_game_difficulty = Boolean(this.selectedBss.show_game_difficulty)
          this.selectedBss.show_game_duration = Boolean(this.selectedBss.show_game_duration)
          this.selectedBss.show_game_title = Boolean(this.selectedBss.show_game_title)
          this.selectedBss.show_number_of_players = Boolean(this.selectedBss.show_number_of_players)
          this.selectedBss.hide_custom_message_when_sold_out = Boolean(this.selectedBss.hide_custom_message_when_sold_out)
          this.games =  _.orderBy(res.data.games, 'position')
          this.payment_settings = res.data.payment_settings

          this.selectedBss.timeout_period = _.find(this.timeout_periods, {id: this.selectedBss.timeout_period})
          this.selectedBss.cart_timeout_period = _.find(this.timeout_periods, {id: this.selectedBss.cart_timeout_period})
          this.selectedBss.default_date_type = _.find(this.default_date_options, {id: this.selectedBss.default_date_type}) || this.default_date_options[0]

          // Build set of linked field IDs from BSS response
          const linkedFieldIds = new Set((res.data.custom_fields || [])
            .filter(cf => cf.active)
            .map(cf => cf.id))
          const bssCustomFields = res.data.custom_fields || []

          // Fetch all group-level custom fields
          let groupFieldsUrl = API_LOCATION + 'companies/' + companyId + '/groups/' + this.group.id + '/custom-fields?count=1000&offset=0&archived=0'
          axios.get(groupFieldsUrl, config)
            .then((groupRes) => {
              let groupFields = groupRes.data.custom_fields || groupRes.data || []

              this.custom_fields = groupFields.map(cf => {
                const bssField = bssCustomFields.find(f => f.id === cf.id)
                return {
                  type: _.find(this.custom_field_types, { id: cf.type }),
                  label: cf.label,
                  id: cf.id,
                  active: linkedFieldIds.has(cf.id) ? 1 : 0,
                  position: bssField ? bssField.position : 999,
                  required: bssField ? bssField.required : cf.required,
                  add_alert: bssField ? bssField.add_alert : cf.add_alert,
                  interaction_required: bssField ? bssField.interaction_required : cf.interaction_required,
                  dropdown_options: cf.dropdown_options || [],
                  external_links: cf.external_links || [],
                  policies: cf.policies || [],
                }
              })

              this.custom_fields.sort((a, b) => {
                if (a.active && !b.active) return -1
                if (!a.active && b.active) return 1
                return (a.position || 0) - (b.position || 0)
              })
            })
            .catch((groupErr) => {
              console.error('Failed to fetch group custom fields', groupErr)
              this.custom_fields = bssCustomFields.map(cf => ({
                type: _.find(this.custom_field_types, { id: cf.type }),
                label: cf.label,
                id: cf.id,
                active: cf.active,
                position: cf.position,
                required: cf.required,
                add_alert: cf.add_alert,
                interaction_required: cf.interaction_required,
                dropdown_options: cf.dropdown_options || [],
                external_links: cf.external_links || [],
                policies: cf.policies || [],
              }))
            })

          this.getIframe()
          this.loading = false
        })
        .catch((err) => {
          this.loading = false
          console.error(err)
          if(err.response.status == 401){
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then((response)=> {
              this.$router.push('/')
            })
          }
          else if(err.response.status == 500){
            this.feature_not_enabled = true
          }
          else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    async getGroupGames() {
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let groupId = this.group.id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId + '/games'

      try {
        let res = await axios.get(url, config)
        this.all_group_games = res.data
      } catch (err) {
        console.error(err)
      }
    },
    getFonts(){
      let url = API_LOCATION + 'fonts'

      axios.get(url)
        .then(response => {
          this.fonts = response.data
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
  },
  mounted(){
    this.currency_symbol = _.find(this.currencies, { code: JSON.parse(localStorage.getItem('group')).currency }).symbol

    const settingsId = this.$route.query.id
    if (settingsId && settingsId !== '') {
      this.getBookingSiteSettings()
    } else {
      this.initializeNewTemplate()
    }
    this.getFonts()
    this.getGroupGames()
    this.loadPromos()
  }
}
</script>
